'use client';

/**
 * React hook for the CRM opportunity tools section in the AI Reply tab.
 *
 * Wires the UI to the two admin endpoints:
 *
 *   - `POST /api/agents/{email,messaging}/[id]/crm-tools/refresh-schema`
 *     → fresh `fieldSchema` + `pipelines` + current scoped settings + drift
 *
 *   - `PATCH /api/agents/{email,messaging}/[id]/crm-tools/settings`
 *     → merge incoming patch into agent config
 *       (`email_agents.config.crm_tools` / `message_agents.config.crm_tools`),
 *       toggle per-agent enabled flags
 *
 * **State model.** The hook holds the server snapshot from the last
 * successful refresh (`schemaState`) and the currently-edited draft
 * per tool (`drafts`). UI components render against the drafts; the
 * save button diffs draft vs snapshot and sends the patch. After a
 * successful save, the hook reloads the schema so stale-chip drift
 * stays consistent.
 *
 * **Race safety.** A generation counter guards async responses so
 * that a fast second click can't rehydrate stale data on top of a
 * newer response. Same pattern as the existing messaging reply-
 * settings component.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/hooks/auth/use-auth';
import { toast } from 'sonner';
import {
  CRM_OPPORTUNITY_TOOL_NAME,
  SYSTEM_FIELD_SCHEMA,
  crmUpdateFieldsSettingsSchema,
  crmMoveStageSettingsSchema,
  type CrmOpportunityToolName,
  type CrmToolSettingsPatch,
  type RefreshCrmToolSchemaSuccess,
} from '@/lib/ai/tools/crm-opportunity/client-types';
import { useTranslations } from "next-intl"


// =====================================================
// Types
// =====================================================

export type CrmToolsChannel = 'email' | 'messaging' | 'voice';

/** UI status reflecting the last refresh attempt. */
export type CrmToolsLoadStatus =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ok'; data: RefreshCrmToolSchemaSuccess }
  | { kind: 'no_binding' }
  | { kind: 'unsupported_provider'; provider: string }
  | { kind: 'fetch_failed'; message?: string }
  | { kind: 'error'; message: string };

/** Editable per-tool draft the multiselects mutate. */
export interface UpdateFieldsDraft {
  enabled: boolean;
  allowed_field_codes: string[];
}

export interface MoveStageDraft {
  enabled: boolean;
  allowed_stage_codes: string[];
}

export interface CrmToolsDrafts {
  [CRM_OPPORTUNITY_TOOL_NAME.updateFields]: UpdateFieldsDraft;
  [CRM_OPPORTUNITY_TOOL_NAME.moveStage]: MoveStageDraft;
}

interface UseCrmToolsConfigOptions {
  agentId: string;
  channel: CrmToolsChannel;
  /** Skip the auto-load on mount (used when the CRM section is lazy-rendered). */
  autoLoad?: boolean;
  /**
   * Fired after every successful settings/enable save. Lets the host page
   * refresh derived state (e.g. the voice tools page reloads its enabled
   * list so a later generic-tool toggle doesn't POST a stale set).
   */
  onSaved?: () => void;
}

/**
 * Why the current draft can't be saved with `enabled: true`.
 *
 * - `null` = draft is shaped correctly; Save proceeds normally.
 * - `'whitelist_empty'` = operator's current whitelist would filter
 *   to zero live entries (e.g. all codes are stale, or nothing is
 *   selected). Backend zod would reject with `.min(1)`.
 *
 * When the operator's intent is `enabled: false`, the save flow
 * routes to a pure-disable path `{ enabled: false }` without
 * `settings`, so the block reason is informational rather than
 * blocking. The UI uses it to surface an inline warning.
 */
export type CrmToolBlockReason = null | 'whitelist_empty';

export interface UseCrmToolsConfigResult {
  status: CrmToolsLoadStatus;
  drafts: CrmToolsDrafts;
  /** True while either the initial refresh or a re-refresh is in flight. */
  isLoading: boolean;
  /** True while a save request is in flight for the named tool. */
  savingTool: CrmOpportunityToolName | null;
  /** Triggered by the "Refresh schema" button and on mount when autoLoad is true. */
  refresh: () => Promise<void>;
  /** Edit the in-memory draft for one tool. */
  setUpdateFieldsDraft: (next: Partial<UpdateFieldsDraft>) => void;
  setMoveStageDraft: (next: Partial<MoveStageDraft>) => void;
  /**
   * Persist the current draft for one tool via PATCH + auto-refresh.
   * Returns `true` when the underlying PATCH succeeded — callers (e.g.
   * the config dialog Save button) use this to decide whether to close
   * the dialog. Returns `false` on validation block, network error,
   * non-2xx response, or any caught exception.
   */
  saveUpdateFields: () => Promise<boolean>;
  saveMoveStage: () => Promise<boolean>;
  /**
   * Tile-switch auto-save handlers. Unlike the dialog-level `save*`
   * methods, these never touch draft settings — they just flip the
   * master toggle via a pure `{ enabled: next }` payload, which maps
   * to the backend's pure-disable path on `false` and to a stored-
   * settings-preserving enable on `true`. Enabling from an empty
   * stored whitelist toasts and aborts without calling the API so
   * the tile switch visually reverts.
   */
  toggleUpdateFieldsEnabled: (next: boolean) => Promise<void>;
  toggleMoveStageEnabled: (next: boolean) => Promise<void>;
  /** Whether the draft has unsaved changes compared to the last server snapshot. */
  isUpdateFieldsDirty: boolean;
  isMoveStageDirty: boolean;
  /** Reason the draft can't be saved while enabled (informational when disabled). */
  updateFieldsBlockReason: CrmToolBlockReason;
  moveStageBlockReason: CrmToolBlockReason;
}

// =====================================================
// Draft builders (pure)
// =====================================================

function emptyDrafts(): CrmToolsDrafts {
  return {
    [CRM_OPPORTUNITY_TOOL_NAME.updateFields]: {
      enabled: false,
      allowed_field_codes: [],
    },
    [CRM_OPPORTUNITY_TOOL_NAME.moveStage]: {
      enabled: false,
      allowed_stage_codes: [],
    },
  };
}

function draftsFromServerSnapshot(
  snapshot: RefreshCrmToolSchemaSuccess
): CrmToolsDrafts {
  const uf = snapshot.settings[CRM_OPPORTUNITY_TOOL_NAME.updateFields];
  const ms = snapshot.settings[CRM_OPPORTUNITY_TOOL_NAME.moveStage];

  // Per-tool enabled state comes from agent config (surfaced by
  // refresh-schema as `enabledTools`), NOT inferred from whether
  // settings are non-null — saved whitelist persists across
  // enable/disable cycles.
  const enabledSet = new Set(snapshot.enabledTools);
  const supportedSet = new Set(snapshot.supportedTools);

  return {
    [CRM_OPPORTUNITY_TOOL_NAME.updateFields]: {
      enabled:
        supportedSet.has(CRM_OPPORTUNITY_TOOL_NAME.updateFields) &&
        enabledSet.has(CRM_OPPORTUNITY_TOOL_NAME.updateFields),
      // Merge live + stale so the UI can render stale codes as disabled chips.
      allowed_field_codes: uf
        ? [...uf.allowed_field_codes, ...uf.stale_field_codes]
        : [],
    },
    [CRM_OPPORTUNITY_TOOL_NAME.moveStage]: {
      enabled:
        supportedSet.has(CRM_OPPORTUNITY_TOOL_NAME.moveStage) &&
        enabledSet.has(CRM_OPPORTUNITY_TOOL_NAME.moveStage),
      allowed_stage_codes: ms
        ? [...ms.allowed_stage_codes, ...ms.stale_stage_codes]
        : [],
    },
  };
}

// =====================================================
// Hook
// =====================================================

const CHANNEL_BASE: Record<CrmToolsChannel, string> = {
  email: '/api/agents/email',
  messaging: '/api/agents/messaging',
  voice: '/api/agents/voice',
};

export function useCrmToolsConfig(
  options: UseCrmToolsConfigOptions
): UseCrmToolsConfigResult {
  const tUi = useTranslations("Feedback.AgentsUseCrmToolsConfig")

  const { agentId, channel, autoLoad = true, onSaved } = options;
  const { fetchWithAuth } = useAuth();

  const [status, setStatus] = useState<CrmToolsLoadStatus>({ kind: 'idle' });
  const [drafts, setDrafts] = useState<CrmToolsDrafts>(emptyDrafts);
  const [savingTool, setSavingTool] = useState<CrmOpportunityToolName | null>(null);

  // Baseline for dirty comparison — updated on every successful refresh.
  const baselineRef = useRef<CrmToolsDrafts>(emptyDrafts());
  // Generation counter for race-safe refresh.
  const refreshGenRef = useRef(0);

  const refresh = useCallback(async () => {
    const gen = ++refreshGenRef.current;
    setStatus({ kind: 'loading' });

    try {
      const res = await fetchWithAuth(
        `${CHANNEL_BASE[channel]}/${agentId}/crm-tools/refresh-schema`,
        { method: 'POST' }
      );

      if (gen !== refreshGenRef.current) return;

      if (res.status === 404) {
        setStatus({ kind: 'no_binding' });
        baselineRef.current = emptyDrafts();
        setDrafts(emptyDrafts());
        return;
      }
      if (res.status === 409) {
        const body = (await res.json()) as { provider?: string };
        setStatus({
          kind: 'unsupported_provider',
          provider: body.provider ?? 'unknown',
        });
        baselineRef.current = emptyDrafts();
        setDrafts(emptyDrafts());
        return;
      }
      if (res.status === 502) {
        const body = (await res.json()) as { message?: string };
        setStatus({ kind: 'fetch_failed', message: body.message });
        return;
      }
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setStatus({
          kind: 'error',
          message: body.error ?? `refresh failed: ${res.status}`,
        });
        return;
      }

      const data = (await res.json()) as RefreshCrmToolSchemaSuccess;
      if (gen !== refreshGenRef.current) return;

      const fresh = draftsFromServerSnapshot(data);
      baselineRef.current = fresh;
      setDrafts(fresh);
      setStatus({ kind: 'ok', data });
    } catch (err) {
      if (gen !== refreshGenRef.current) return;
      setStatus({
        kind: 'error',
        message: err instanceof Error ? err.message : 'refresh failed',
      });
    }
  }, [agentId, channel, fetchWithAuth]);

  useEffect(() => {
    if (!autoLoad) return;
    void refresh();
  }, [autoLoad, refresh]);

  // ---- draft editors ----

  const setUpdateFieldsDraft = useCallback((next: Partial<UpdateFieldsDraft>) => {
    setDrafts((prev) => ({
      ...prev,
      [CRM_OPPORTUNITY_TOOL_NAME.updateFields]: {
        ...prev[CRM_OPPORTUNITY_TOOL_NAME.updateFields],
        ...next,
      },
    }));
  }, []);

  const setMoveStageDraft = useCallback((next: Partial<MoveStageDraft>) => {
    setDrafts((prev) => ({
      ...prev,
      [CRM_OPPORTUNITY_TOOL_NAME.moveStage]: {
        ...prev[CRM_OPPORTUNITY_TOOL_NAME.moveStage],
        ...next,
      },
    }));
  }, []);

  // ---- save ----

  const saveOne = useCallback(
    async (
      toolName: CrmOpportunityToolName,
      body: { enabled?: boolean; settings?: CrmToolSettingsPatch }
    ): Promise<boolean> => {
      setSavingTool(toolName);
      try {
        const res = await fetchWithAuth(
          `${CHANNEL_BASE[channel]}/${agentId}/crm-tools/settings`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ toolName, ...body }),
          }
        );

        if (!res.ok) {
          const err = (await res.json().catch(() => ({}))) as {
            error?: string;
            details?: string;
          };
          const message = err.details ?? err.error ?? `save failed: ${res.status}`;
          toast.error(tUi("failedToSaveValue0Value1", { value0: String(toolName), value1: String(message) }));
          return false;
        }

        toast.success(tUi("cRMToolSettingsSaved"));
        await refresh();
        onSaved?.();
        return true;
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : 'CRM tool settings save failed'
        );
        return false;
      } finally {
        setSavingTool(null);
      }
    },
    [fetchWithAuth, channel, agentId, tUi, refresh, onSaved]
  );

  // ---- candidate payload builders (pure) ----

  const updateFieldsCandidate = buildUpdateFieldsCandidate(
    drafts[CRM_OPPORTUNITY_TOOL_NAME.updateFields],
    status
  );
  const moveStageCandidate = buildMoveStageCandidate(
    drafts[CRM_OPPORTUNITY_TOOL_NAME.moveStage],
    status
  );

  const saveUpdateFields = useCallback(async (): Promise<boolean> => {
    const draft = drafts[CRM_OPPORTUNITY_TOOL_NAME.updateFields];
    const baseline = baselineRef.current[CRM_OPPORTUNITY_TOOL_NAME.updateFields];

    // Only the master toggle changed — pure toggle, don't touch
    // settings. This keeps disabling a tool from failing on invalid
    // whitelist state (the backend's pure-disable path matches).
    if (draft.enabled !== baseline.enabled && settingsUnchanged(draft, baseline)) {
      return saveOne(CRM_OPPORTUNITY_TOOL_NAME.updateFields, {
        enabled: draft.enabled,
      });
    }

    // Settings changed (maybe alongside enabled).
    if (updateFieldsCandidate.blockReason !== null) {
      // Computed payload would be rejected by the backend's zod gate.
      if (!draft.enabled) {
        // Intent is "turn off" — honor it via pure disable, drop the
        // invalid settings edit silently (operator can fix on re-enable).
        return saveOne(CRM_OPPORTUNITY_TOOL_NAME.updateFields, { enabled: false });
      }
      toast.error(
        updateFieldsCandidate.blockReason === 'whitelist_empty'
          ? 'Select at least one field before enabling'
          : 'Invalid settings'
      );
      return false;
    }

    return saveOne(CRM_OPPORTUNITY_TOOL_NAME.updateFields, {
      enabled: draft.enabled,
      settings: updateFieldsCandidate.patch!,
    });
  }, [drafts, saveOne, updateFieldsCandidate]);

  const saveMoveStage = useCallback(async (): Promise<boolean> => {
    const draft = drafts[CRM_OPPORTUNITY_TOOL_NAME.moveStage];
    const baseline = baselineRef.current[CRM_OPPORTUNITY_TOOL_NAME.moveStage];

    if (draft.enabled !== baseline.enabled && settingsUnchanged(draft, baseline)) {
      return saveOne(CRM_OPPORTUNITY_TOOL_NAME.moveStage, {
        enabled: draft.enabled,
      });
    }

    if (moveStageCandidate.blockReason !== null) {
      if (!draft.enabled) {
        return saveOne(CRM_OPPORTUNITY_TOOL_NAME.moveStage, { enabled: false });
      }
      toast.error(tUi("selectAtLeastOneStageBeforeEnabling"));
      return false;
    }

    return saveOne(CRM_OPPORTUNITY_TOOL_NAME.moveStage, {
      enabled: draft.enabled,
      settings: moveStageCandidate.patch!,
    });
  }, [drafts, moveStageCandidate.blockReason, moveStageCandidate.patch, saveOne, tUi]);

  // ---- tile-switch auto-save (pure toggle) ----

  const toggleUpdateFieldsEnabled = useCallback(
    async (next: boolean) => {
      if (next === true && status.kind === 'ok') {
        const stored = status.data.settings[CRM_OPPORTUNITY_TOOL_NAME.updateFields];
        if (!stored || stored.allowed_field_codes.length === 0) {
          toast.error(tUi("configureAllowedFieldsBeforeEnablingThisTool"));
          return;
        }
      }
      await saveOne(CRM_OPPORTUNITY_TOOL_NAME.updateFields, { enabled: next });
    },
    [status, saveOne, tUi]
  );

  const toggleMoveStageEnabled = useCallback(
    async (next: boolean) => {
      if (next === true && status.kind === 'ok') {
        const stored = status.data.settings[CRM_OPPORTUNITY_TOOL_NAME.moveStage];
        if (!stored || stored.allowed_stage_codes.length === 0) {
          toast.error(tUi("configureAllowedStagesBeforeEnablingThisTool"));
          return;
        }
      }
      await saveOne(CRM_OPPORTUNITY_TOOL_NAME.moveStage, { enabled: next });
    },
    [status, saveOne, tUi]
  );

  // ---- dirty tracking ----

  const isUpdateFieldsDirty = !draftEqual(
    drafts[CRM_OPPORTUNITY_TOOL_NAME.updateFields],
    baselineRef.current[CRM_OPPORTUNITY_TOOL_NAME.updateFields]
  );
  const isMoveStageDirty = !draftEqual(
    drafts[CRM_OPPORTUNITY_TOOL_NAME.moveStage],
    baselineRef.current[CRM_OPPORTUNITY_TOOL_NAME.moveStage]
  );

  return {
    status,
    drafts,
    isLoading: status.kind === 'loading',
    savingTool,
    refresh,
    setUpdateFieldsDraft,
    setMoveStageDraft,
    saveUpdateFields,
    saveMoveStage,
    toggleUpdateFieldsEnabled,
    toggleMoveStageEnabled,
    isUpdateFieldsDirty,
    isMoveStageDirty,
    updateFieldsBlockReason: updateFieldsCandidate.blockReason,
    moveStageBlockReason: moveStageCandidate.blockReason,
  };
}

// =====================================================
// Draft equality (pure)
// =====================================================

function arraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function draftEqual(a: UpdateFieldsDraft | MoveStageDraft, b: UpdateFieldsDraft | MoveStageDraft): boolean {
  if (a.enabled !== b.enabled) return false;
  if ('allowed_field_codes' in a && 'allowed_field_codes' in b) {
    return arraysEqual(a.allowed_field_codes, b.allowed_field_codes);
  }
  if ('allowed_stage_codes' in a && 'allowed_stage_codes' in b) {
    return arraysEqual(a.allowed_stage_codes, b.allowed_stage_codes);
  }
  return false;
}

// =====================================================
// Candidate builders (pure)
// =====================================================

interface CrmToolCandidate {
  /** The patch to send to the backend. `null` when `blockReason` is set. */
  patch: CrmToolSettingsPatch | null;
  /** Non-null when the operator's current draft can't be saved while enabled. */
  blockReason: CrmToolBlockReason;
}

/**
 * Build the outgoing `settings` patch for `crm_update_opportunity_fields`
 * from the current draft, filtering out stale codes (codes that are no
 * longer in the live schema). Returns a `blockReason` when the filtered
 * result would fail the backend's zod check — the save flow uses this
 * to either route to pure-disable or to surface an inline warning.
 */
function buildUpdateFieldsCandidate(
  draft: UpdateFieldsDraft,
  status: CrmToolsLoadStatus
): CrmToolCandidate {
  if (status.kind !== 'ok') {
    return { patch: null, blockReason: null };
  }

  const live = new Set<string>();
  for (const field of status.data.fieldSchema) live.add(field.code);
  for (const field of status.data.systemFieldSchema ?? SYSTEM_FIELD_SCHEMA) {
    live.add(field.code);
  }

  const keptCodes = draft.allowed_field_codes.filter((c) => live.has(c));
  const candidate: CrmToolSettingsPatch = {
    allowed_field_codes: keptCodes,
  };

  const parseResult = crmUpdateFieldsSettingsSchema.safeParse(candidate);
  if (!parseResult.success) {
    return { patch: null, blockReason: 'whitelist_empty' };
  }
  return { patch: candidate, blockReason: null };
}

/**
 * Same idea for `crm_move_opportunity_stage`: filter stale stage ids
 * and run client-side zod validation before building the patch.
 *
 * There is no pipeline-whitelist concept anymore — the LLM always
 * receives both `stage` and `pipeline` as required parameters at
 * tool-schema level, so operators only need to pick stages.
 */
function buildMoveStageCandidate(
  draft: MoveStageDraft,
  status: CrmToolsLoadStatus
): CrmToolCandidate {
  if (status.kind !== 'ok') {
    return { patch: null, blockReason: null };
  }

  const liveStageIds = new Set<string>();
  for (const p of status.data.pipelines) {
    for (const s of p.stages) liveStageIds.add(s.id);
  }

  const keptStages = draft.allowed_stage_codes.filter((c) => liveStageIds.has(c));

  const parseResult = crmMoveStageSettingsSchema.safeParse({
    allowed_stage_codes: keptStages,
  });
  if (!parseResult.success) {
    return { patch: null, blockReason: 'whitelist_empty' };
  }

  const patch: CrmToolSettingsPatch = {
    allowed_stage_codes: keptStages,
  };
  return { patch, blockReason: null };
}

/**
 * Diff helper that returns `true` when only the `enabled` toggle
 * changed and all settings fields match the baseline — used by the
 * save flow to route pure-toggle changes through the
 * `{ enabled }` path without touching stored settings.
 */
function settingsUnchanged(
  draft: UpdateFieldsDraft | MoveStageDraft,
  baseline: UpdateFieldsDraft | MoveStageDraft
): boolean {
  if ('allowed_field_codes' in draft && 'allowed_field_codes' in baseline) {
    return arraysEqual(draft.allowed_field_codes, baseline.allowed_field_codes);
  }
  if ('allowed_stage_codes' in draft && 'allowed_stage_codes' in baseline) {
    return arraysEqual(draft.allowed_stage_codes, baseline.allowed_stage_codes);
  }
  return false;
}

// Exported solely for the associated unit tests, if any are added later.
export const __internals = {
  draftsFromServerSnapshot,
  emptyDrafts,
  draftEqual,
  buildUpdateFieldsCandidate,
  buildMoveStageCandidate,
  settingsUnchanged,
};
